import { html, LitElement, type PropertyValues, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { paintTick } from '../../base/package/util/render';
import style from './ui-column-orchestrator.css?inline';

const CName = 'ui-column-orchestrator';

function pxToRem(pxValue: number): number {
  return pxValue / 16;
}

const COLUMN_WIDTH = pxToRem(148);
const GAP = pxToRem(8);
export const COLUMNS_NUMBER = 8;

type Column = {
  id: string;
};

// Module-level WeakMap to associate observers with their host components.
// Prevents the observer callback from closing over `this`, which would
// retain the component via Chrome's IntersectionObserverController (IGM-3644).
const observerHosts = new WeakMap<IntersectionObserver, WeakRef<UIColumnOrchestrator>>();

function handleIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
  const hostRef = observerHosts.get(observer);
  const host = hostRef?.deref();
  if (!host) return;

  entries.forEach((entry) => {
    const columnIndex = Number((entry.target as HTMLElement).dataset.colIdx);
    if (entry.isIntersecting && columnIndex !== undefined) {
      if (!host.visibleColumns.includes(columnIndex)) {
        host.visibleColumns = [...host.visibleColumns, columnIndex];
      }
    } else {
      host.visibleColumns = host.visibleColumns.filter((id) => id !== columnIndex);
    }
  });

  host.dispatchEvent(
    new CustomEvent<number[]>('on-visible-columns-change', {
      detail: host.visibleColumns ?? undefined,
      bubbles: true,
      composed: true,
    }),
  );
}

@customElement(CName)
export class UIColumnOrchestrator extends LitElement {
  static readonly styles = unsafeCSS(style);
  @property({ type: Array }) columns: Column[] = [];
  @property({ type: Boolean }) reduceMotion = false;
  @property() columnRenderFunction: (card: Column, index: number) => TemplateResult<1>;
  @state() _renderColumns: Column[] = [];
  @state() visibleColumns: number[] = [];
  private _oldColumns: Column[] = [];
  private _oldColumnsIds: string[] = [];
  private _observer: IntersectionObserver | null = null;
  private readonly _observedElements = new Set<Element>();

  willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('columns')) {
      // On first render, just mirror columns
      if (!this._renderColumns.length && this.columns.length) {
        this._renderColumns = [...this.columns];
      } else if (this._renderColumns.length) {
        this._onColumnsUpdate();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.setProperty('--column-width', `${COLUMN_WIDTH}rem`);
    this.style.setProperty('--gap', `${GAP}rem`);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._observer) {
      observerHosts.delete(this._observer);
      this._observer.disconnect();
      this._observer = null;
    }
    this._observedElements.clear();
  }

  firstUpdated() {
    this._updateOldColumns();
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('columns')) {
      paintTick().then(() => {
        this._observeColumnsVisibility();
      });
    }
  }

  render() {
    return html`
      <ul class="columns-container">
        ${
          this.columns?.length
            ? repeat(
                this._renderColumns,
                (card) => card.id,
                (card, index) => html`
                <li class="column" data-col-id="${card.id}" data-col-idx="${index}">
                  ${this.columnRenderFunction(card, index)}
                </li>
              `,
              )
            : Array.from({ length: 8 }, () => html`<li class="column--placeholder"></li>`)
        }
      </ul>
    `;
  }

  private _updateOldColumns() {
    this._oldColumns = [...this._renderColumns];
    this._oldColumnsIds = this._oldColumns.map((card) => card.id);
  }

  private _onColumnsUpdate() {
    // TODO: check if still needed
    if (!this._oldColumns) {
      // if we have no old columns stored, do nothing special
      this._updateOldColumns();
      return;
    }

    const columnIds = this.columns.map((column) => column.id);
    const hasDuplicates = new Set(columnIds).size !== columnIds.length;

    if (hasDuplicates) {
      window.$app.logger.error('Activity lane error', 'duplicates are detected');
    }

    const enteringColumns = this.columns.filter(
      (newColumn) => !this._oldColumns!.some((oldColumn: Column) => newColumn.id === oldColumn.id),
    );

    const leavingColumns = this._oldColumns.filter(
      (oldColumn) => !this.columns.some((newColumn) => newColumn.id === oldColumn.id),
    );

    if (enteringColumns?.length && leavingColumns?.length) {
      // place newly entered at the end (they will not be visible yet)
      this._renderColumns = [...this._oldColumns, ...enteringColumns];
      this._updateOldColumns();
      // show animation (swap leaving columns with entering ones)
      this._renderColumns = [...this.columns, ...leavingColumns];
      this._animateReorder();
      return;
    }

    this._renderColumns = [...this.columns];
    this._animateReorder();
  }

  private _animateReorder() {
    if (this._oldColumns.length === 0) {
      // prevent sliding animation on the first render
      this._updateOldColumns();
      return;
    }

    if (this.reduceMotion) {
      // skip animation, just update tracking state instantly
      this._updateOldColumns();
      return;
    }

    // defer animation until after the new DOM is painted
    requestAnimationFrame(() => {
      this._renderColumns.forEach((card, newIndex) => {
        const el: HTMLElement | null = this.renderRoot.querySelector(`[data-col-id="${card.id}"]`);
        if (!el) return;

        const oldIndex = this._oldColumnsIds.indexOf(card.id);
        let deltaX: number;

        if (oldIndex === -1) {
          // This is a brand-new column, slide in from the right
          deltaX = 300;
        } else {
          // Existing column: compute shift based on old vs. new index
          deltaX = (oldIndex - newIndex) * (COLUMN_WIDTH + GAP);
        }

        const isMovingLeft = oldIndex > newIndex;
        const isEntering = oldIndex >= COLUMNS_NUMBER;
        const isLeaving = newIndex >= COLUMNS_NUMBER;

        if (deltaX !== 0) {
          // temporarily place the column in its old position
          el.style.transition = 'none';
          el.style.transform = `translateX(${deltaX}rem)`;

          if ((isMovingLeft && !isEntering) || isLeaving) {
            el.style.opacity = '0.3';
          }

          // force a layout reflow to apply the transform
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          el.offsetWidth;

          // animate to its new position
          el.style.transition = 'transform 300ms ease, opacity 500ms ease-in-out';
          el.style.transform = 'translateX(0)';
          el.style.opacity = isLeaving ? '0' : '1';
        }
      });

      this._updateOldColumns();
    });
  }

  private _observeColumnsVisibility() {
    const columns = this.shadowRoot?.querySelectorAll('.column');
    if (!columns?.length) return;

    if (!this._observer) {
      this._observer = new IntersectionObserver(handleIntersection, { root: document.body });
      observerHosts.set(this._observer, new WeakRef(this));
    }

    // Unobserve elements no longer in DOM
    for (const el of this._observedElements) {
      if (!el.isConnected) {
        this._observer.unobserve(el);
        this._observedElements.delete(el);
      }
    }

    // Observe new elements
    columns.forEach((column) => {
      if (!this._observedElements.has(column)) {
        this._observer!.observe(column);
        this._observedElements.add(column);
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIColumnOrchestrator;
  }
}
